import React, { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { formatRelativeTime, formatAbsoluteDate, truncateText, stripHtmlTags } from './utils';
import { HolographicCard } from './components/ui/holographic-card';
import { MorphingSquare } from './components/ui/morphing-square';

const CACHE_TTL      = 5 * 60 * 1000;
const HISTORY_WINDOW = 90 * 24 * 60 * 60 * 1000; // 90-day accumulation
const RECENT_WINDOW  = 14 * 24 * 60 * 60 * 1000; // 14-day "recent" divider

// ─── Cache (accumulating) ─────────────────────────────────────────────────────

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { items, ts } = JSON.parse(raw);
    return { items: items || [], stale: Date.now() - ts > CACHE_TTL };
  } catch {
    return null;
  }
};

const mergeAndStore = (key, newItems, storedItems) => {
  try {
    const cutoff = Date.now() - HISTORY_WINDOW;
    const seen   = new Set(newItems.map(i => i.link));
    const merged = [...newItems];
    for (const item of storedItems) {
      if (!seen.has(item.link)) { merged.push(item); seen.add(item.link); }
    }
    merged.sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
    const pruned = merged.filter(i => {
      const t = i.pubDate ? new Date(i.pubDate).getTime() : 0;
      return t >= cutoff;
    });
    localStorage.setItem(key, JSON.stringify({ items: pruned, ts: Date.now() }));
    return pruned;
  } catch {
    return newItems;
  }
};

// ─── Fetch via Lambda proxy ───────────────────────────────────────────────────
// The RSS feeds are limited to ~20 items (2 days for AWS, ~1 week for others).
// The accumulating localStorage cache below grows the visible history over time.
// To unlock full 1–3 month history immediately, the Lambda backend needs to
// call each provider's paginated JSON API and return more items.
//
// AWS:   https://aws.amazon.com/api/dirs/items/search
//          ?item.directoryId=whats-new-content-all&sort_by=item.additionalFields.postDateTime
//          &sort_order=desc&size=300
// Azure: https://www.microsoft.com/releasecommunications/api/v2/azure/rss (current RSS)
// GCP:   https://cloudblog.withgoogle.com/rss/ (current RSS)

const API_GATEWAY_URL = 'https://lu3ia6zt3f.execute-api.ap-southeast-1.amazonaws.com/prod';

const fetchItems = async (feedUrl) => {
  const res = await fetch(`${API_GATEWAY_URL}/rss?url=${encodeURIComponent(feedUrl)}`);
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  const data = await res.json();
  return data.items || [];
};

// ─── Item card ────────────────────────────────────────────────────────────────

const FeedItemCard = memo(({ item, provider, isExpanded, onToggle }) => {
  const cleanDesc = item.description ? stripHtmlTags(item.description) : '';
  const truncDesc = cleanDesc ? truncateText(cleanDesc, 120) : '';
  const hasDesc   = cleanDesc.trim().length > 0;
  return (
    <HolographicCard provider={provider} disabled={isExpanded}>
      <div className="feed-item">
        <a href={item.link} target="_blank" rel="noopener noreferrer" className="feed-item-title">
          {item.title}
        </a>
        <div className="feed-item-meta-row">
          <span className="feed-item-date">{formatRelativeTime(item.pubDate)}</span>
          {hasDesc && (
            <button
              className="feed-expand-btn"
              onClick={() => onToggle(item.link)}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? '▲ Less' : '▼ More'}
            </button>
          )}
        </div>
        {hasDesc && (
          <p className={`feed-item-desc${isExpanded ? ' expanded' : ''}`}>
            {isExpanded ? cleanDesc : truncDesc}
          </p>
        )}
      </div>
    </HolographicCard>
  );
});

FeedItemCard.displayName = 'FeedItemCard';

// ─── Main component ───────────────────────────────────────────────────────────

const RSSFeedDisplay = memo(({ feedUrl, title, provider, logoUrl, searchQuery = '', regionQuery = [] }) => {
  const [feedItems,     setFeedItems]     = useState(null);
  const [error,         setError]         = useState(null);
  const [isLoading,     setIsLoading]     = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showOlder,     setShowOlder]     = useState(false);

  const toggleExpand = useCallback((link) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      next.has(link) ? next.delete(link) : next.add(link);
      return next;
    });
  }, []);

  useEffect(() => {
    const cacheKey = `rss_${btoa(feedUrl).slice(0, 32)}`;

    const load = async (background = false, storedItems = []) => {
      if (!background) setIsLoading(true);
      try {
        const newItems = await fetchItems(feedUrl);
        const merged   = mergeAndStore(cacheKey, newItems, storedItems);
        setFeedItems(merged);
        setError(null);
      } catch (err) {
        console.error('Error fetching feed:', err);
        if (!background) setError('Failed to load feed. Please try again later.');
      } finally {
        if (!background) setIsLoading(false);
      }
    };

    const cached = readCache(cacheKey);
    if (cached) {
      setFeedItems(cached.items);
      setIsLoading(false);
      if (cached.stale) load(true, cached.items);
    } else {
      load(false, []);
    }
  }, [feedUrl]);

  // ── Filter ────────────────────────────────────────────────────────────────

  const filteredItems = useMemo(() => {
    if (!feedItems) return [];
    const textOf = (item) =>
      ((item.title || '') + ' ' + (item.description ? stripHtmlTags(item.description) : '')).toLowerCase();
    const matchesRegion = (item) =>
      !regionQuery.length || regionQuery.some(t => textOf(item).includes(t.toLowerCase()));
    const matchesSearch = (item) =>
      !searchQuery || textOf(item).includes(searchQuery.toLowerCase());
    return feedItems.filter(item => matchesRegion(item) && matchesSearch(item));
  }, [feedItems, searchQuery, regionQuery]);

  // ── Split recent / older ──────────────────────────────────────────────────

  const { recentItems, olderItems } = useMemo(() => {
    const cutoff = Date.now() - RECENT_WINDOW;
    const recent = [], older = [];
    for (const item of filteredItems) {
      const t = item.pubDate ? new Date(item.pubDate).getTime() : 0;
      (t >= cutoff ? recent : older).push(item);
    }
    return { recentItems: recent, olderItems: older };
  }, [filteredItems]);

  const oldestDate = useMemo(() => {
    if (!filteredItems.length) return null;
    const last = filteredItems[filteredItems.length - 1];
    return last?.pubDate ? formatAbsoluteDate(last.pubDate) : null;
  }, [filteredItems]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className={`rss-feed ${provider} loading`}>
        <MorphingSquare message="Loading..." className="bg-white/70" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rss-feed ${provider} error`}>
        <p className="error-message">{error}</p>
      </div>
    );
  }

  const renderItem = (item, index) => (
    <li key={`${item.link}-${index}`} className="feed-item-wrapper">
      <FeedItemCard
        item={item}
        provider={provider}
        isExpanded={expandedItems.has(item.link)}
        onToggle={toggleExpand}
      />
    </li>
  );

  return (
    <div className={`rss-feed ${provider}`}>
      <div className="provider-header">
        <a href={feedUrl} target="_blank" rel="noopener noreferrer">
          <img src={logoUrl} alt={title} className="provider-logo" />
        </a>
        <div className="provider-header-text">
          <span className="provider-name">{title}</span>
          {oldestDate && (
            <span className="provider-feed-meta">
              {filteredItems.length} items · since {oldestDate}
            </span>
          )}
        </div>
      </div>

      <div className="rss-feed-content">
        {filteredItems.length === 0 ? (
          <div className="no-items">
            <p>
              {searchQuery || regionQuery.length
                ? 'No items match your filters.'
                : 'No items found in the feed.'}
            </p>
          </div>
        ) : (
          <>
            {recentItems.length > 0 && (
              <ul className="feed-items-list">
                {recentItems.map(renderItem)}
              </ul>
            )}

            {olderItems.length > 0 && (
              <div className="older-section">
                <button
                  className="older-toggle"
                  onClick={() => setShowOlder(v => !v)}
                  aria-expanded={showOlder}
                >
                  <span className="older-toggle-line" />
                  <span className="older-toggle-label">
                    {showOlder
                      ? `Hide ${olderItems.length} older items`
                      : `Show ${olderItems.length} older items (since ${oldestDate})`}
                  </span>
                  <span className="older-toggle-line" />
                </button>
                {showOlder && (
                  <ul className="feed-items-list older-items-list">
                    {olderItems.map(renderItem)}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

RSSFeedDisplay.displayName = 'RSSFeedDisplay';

export default RSSFeedDisplay;
